import { useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, Navigate, useLocation } from 'react-router-dom';

import CssBaseline from '@material-ui/core/CssBaseline';
import { Drawer, AppBar } from '@material-ui/core';
import { ThemeProvider } from '@material-ui/core/styles';

import Worker from '@/utils/background-tasks';
import { useThemeHelper } from '@tcap/data-router/hooks/';
import { traceBoot } from '@tcap/data-router/utils/bootTrace';
import { DashboardMenuProvider } from '@/context/DashboardMenuContext';
import { LayoutProvider } from '@/context/LayoutContext';
import Sidebar from '@/components/sidebar/Sidebar';
import Navbar from '@/pages/navbar/Navbar';
import WholesalerPaymentBanner from '@/components/banners/WholesalerPaymentBanner';

import '@/App.css';
import '@/console.js';

function ProtectedLayout() {
    const hasLoggedRenderRef = useRef(false);
    const dispatch = useDispatch();
    const {
        classes,
        mixedMode: lightMode,
        lightModeTheme,
        theme,
    } = useThemeHelper();

    const {
        token,
        reLogin,
        showMFA,
        defaultPartner,
        defaultCompany,
        defaultWholesaler,
    } = useSelector((state) => state.login);
    const { currentWholesaler } = useSelector((state) => state.navigationLists);

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);

    if (!hasLoggedRenderRef.current) {
        hasLoggedRenderRef.current = true;
        traceBoot('protected-layout.render', {
            pathname: location.pathname,
        });
    }

    const wholesalerID = queryParams.get('wholesalerID');
    const partnerID = queryParams.get('partnerID');
    const companyID = queryParams.get('companyID');

    useEffect(() => {
        if (
            (defaultPartner || defaultCompany || defaultWholesaler) &&
            !currentWholesaler
        ) {
            window.history.replaceState(null, '', '/ui' + location.pathname);
            //if current stuffs arent set yet, set them to default.
            dispatch({
                type: 'CHANGE_CURRENT_PARTNER',
                payload: partnerID || defaultPartner,
            });
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY',
                payload: companyID || defaultCompany,
            });
            dispatch({
                type: 'CHANGE_CURRENT_WHOLESALER',
                payload: wholesalerID || defaultWholesaler,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentWholesaler]);

    useLayoutEffect(() => {
        traceBoot('protected-layout.commit', {
            pathname: location.pathname,
        });
    }, [location.pathname]);

    useEffect(() => {
        traceBoot('protected-layout.mount', {
            pathname: location.pathname,
        });
    }, [location.pathname]);

    // NOTE: Hot fix for #2187 and #2554 AG Grid popups not closing when clicking outside of them.
    useEffect(() => {
        const handleClickOutsidePopup = (event) => {
            const popups = document.querySelectorAll('.ag-popup');
            const clickedInsideAny = Array.from(popups).some((popup) =>
                popup.contains(event.target),
            );
            if (!clickedInsideAny) {
                popups.forEach((popup) => popup.remove());
            }
        };

        // Capture phase so this runs before AG Grid's own mousedown handlers
        // (e.g. the pagination page-size picker) open a new popup — otherwise
        // the just-opened popup gets treated as a stale one and removed
        // immediately, since it's appended under document.body rather than
        // nested inside the element that was clicked.
        document.addEventListener('mousedown', handleClickOutsidePopup, true);
        return () =>
            document.removeEventListener(
                'mousedown',
                handleClickOutsidePopup,
                true,
            );
    }, []);

    if (!token || reLogin || showMFA) {
        traceBoot('protected-layout.navigate-login', {
            pathname: location.pathname,
        });
        return <Navigate to="/" replace />;
    }

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <div className={classes.root}>
                <LayoutProvider>
                    <ThemeProvider theme={lightMode ? lightModeTheme : theme}>
                        <AppBar position="fixed" className={classes.appBar}>
                            <Worker />
                            <WholesalerPaymentBanner />
                            <Navbar />
                        </AppBar>
                        <Drawer
                            className={`${classes.drawer}`}
                            variant="permanent"
                            classes={{
                                paper: classes.drawerPaper,
                            }}
                            style={{
                                transition: 'width 0.4s ease-in-out',
                            }}>
                            <Sidebar />
                        </Drawer>
                    </ThemeProvider>
                    <main className={classes.content}>
                        <DashboardMenuProvider>
                            <Outlet />
                        </DashboardMenuProvider>
                    </main>
                </LayoutProvider>
            </div>
        </ThemeProvider>
    );
}

export default ProtectedLayout;
